
import React, { useState } from 'react';
import { WorkerProfile } from '../types';
import { Star, MapPin, BadgeCheck, Clock, ChevronRight, Zap } from 'lucide-react';

interface WorkerCardProps {
  worker: WorkerProfile;
  onClick: () => void;
  onBook?: () => void;
  isSelected?: boolean;
}

export const WorkerCard: React.FC<WorkerCardProps> = ({ worker, onClick, onBook, isSelected }) => {
  // Mock ETA calculation: 5 mins base + 4 mins per km
  const eta = Math.ceil(5 + (worker.distanceKm * 4));
  const [imageError, setImageError] = useState(false);

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-2xl p-3 border transition-all duration-300
        ${isSelected
          ? 'bg-blue-50/50 dark:bg-blue-900/20 border-blue-600 dark:border-blue-500 ring-1 ring-blue-600 dark:ring-blue-500 shadow-md transform scale-[1.01]'
          : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-700 hover:shadow-lg'
        }
      `}
    >
      <div className="flex gap-3 items-start">
        {/* Avatar Section */}
        <div className="relative shrink-0">
          <img
            src={imageError ? '/default-avatar.png' : (worker.avatarUrl || '/default-avatar.png')}
            alt={worker.name}
            onError={() => setImageError(true)}
            className="w-12 h-12 rounded-xl object-cover shadow-sm bg-slate-50 dark:bg-slate-800"
          />
          {worker.isOnline && (
            <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-[2px] border-white dark:border-slate-900 rounded-full"></span>
          )}
        </div>

        {/* Info Section */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-0.5">
            <h3 className="text-sm font-black text-slate-900 dark:text-white truncate flex items-center gap-1">
              {worker.name}
              {worker.verificationStatus === 'verified' && (
                <BadgeCheck className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 fill-blue-50 dark:fill-blue-900/30" />
              )}
            </h3>
            <div className="flex flex-col items-end leading-none">
              {/* Removed Price Display */}
            </div>
          </div>

          <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate mb-1">{worker.skill}</p>

          <div className="flex items-center gap-2 text-[9px] text-slate-500 dark:text-slate-400 font-bold mb-1">
            <span className="flex items-center gap-0.5 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">
              <Star className="w-2.5 h-2.5 text-yellow-500 fill-yellow-500" /> {worker.rating}
            </span>
            <span className="flex items-center gap-0.5 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">
              <MapPin className="w-2.5 h-2.5 text-slate-400" /> {worker.distanceKm} km
            </span>
            <span className="flex items-center gap-0.5 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">
              <Clock className="w-2.5 h-2.5 text-slate-400" /> ~{eta}m
            </span>
          </div>
        </div>
      </div>

      {/* Tags Section */}
      {worker.tags && worker.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {worker.tags.slice(0, 3).map((tag, i) => (
            <span key={i} className="text-[8px] font-black uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-100 dark:border-slate-700">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer / Action Section - Full Width Button */}
      {onBook && (
        <div className="mt-3 pt-3 border-t border-slate-50 dark:border-slate-800 w-full">
          {worker.isOnline ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBook();
              }}
              className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md shadow-blue-600/30 active:scale-95 flex items-center justify-center gap-2"
            >
              <Zap className="w-3.5 h-3.5 text-white fill-white" />
              Book Now
            </button>
          ) : (
            <div className="w-full bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Offline
            </div>
          )}
        </div>
      )}
    </div>
  );
};
